import amazonAds from './amazonAds';
import googleTags from './google';
import metaPixel from './metaPixel';
import tddPixel from './tdd';
import { BrandclubEventType, SimpleTrackingProvider, TrackingPixel } from './types';
import { parseEventAndPayload } from './utils';

const randomKey = 'd24buQs3x5ag';
let debug = false;

const log = (...args: any[]) => debug &&
  // eslint-disable-next-line no-console
  console.info(...['[externalTracking]'].concat(args));

// Registry of tracking providers
const trackingProviders: Record<string, SimpleTrackingProvider> = {
  Meta: {
    enableDebug: () => metaPixel.enableDebug(),
    track: ({ brandId, pixels, eventName, eventType, eventPayload }) =>
      metaPixel.track({ brandId, pixels, eventName, eventType, eventPayload })
  },
  GoogleAds: {
    enableDebug: () => googleTags.enableDebug(),
    track: ({ brandId, pixels, eventName, eventType, eventPayload }) =>
      googleTags.track({ brandId, pixels, eventName, eventType, eventPayload })
  },
  TTD: {
    enableDebug: () => tddPixel.enableDebug(),
    track: ({ brandId, pixels, eventName, eventType, eventPayload }) =>
      tddPixel.track({ brandId, pixels, eventName, eventType, eventPayload })
  },
  AmazonAds: {
    enableDebug: () => amazonAds.enableDebug(),
    track: ({ brandId, pixels, eventName, eventType, eventPayload }) =>
      amazonAds.track({ brandId, pixels, eventName, eventType, eventPayload })
  }
};

/**
 * Register a new tracking provider
 */
export function registerTrackingProvider(
  providerId: string,
  provider: SimpleTrackingProvider
): void {
  trackingProviders[providerId] = provider;
  log(`Registered tracking provider: ${providerId}`);
}

/**
 * Enable debug mode for all providers
 */
export function enableDebug() {
  debug = true;
  log('Enabling debug mode');
  Object.values(trackingProviders).forEach(provider => provider.enableDebug());
}

export function setIdentity(identity: string) {
  metaPixel.setIdentity(identity);
}

/**
 * Track an event with all registered providers
 */
export function track(
  event: string,
  params: { [key: string]: any },
  pixelContainer: { brandId: string, pixels: TrackingPixel[] }
) {
  // Handle debug flag
  if (params?.[randomKey]) {
    enableDebug();
    params[randomKey] = undefined;
  }

  log('track', { event, params, pixelContainer });
  if (!pixelContainer?.brandId && !pixelContainer?.pixels?.length) {
    // check checkoutitems array and if it has length 1 then copy over all the params from the first item
    if (params?.checkoutItems?.length === 1) {
      const firstItem = params.checkoutItems[0];
      params = {
        ...params,
        brandId: firstItem.brandId,
        brandName: firstItem.brandName,
        categoryId: firstItem.categoryId,
        categoryName: firstItem.categoryName,
        subCategoryId: firstItem.subCategoryId,
        subCategoryName: firstItem.subCategoryName,
        retailerId: firstItem.retailerId,
        retailerSku: firstItem.retailerSku,
        stacklineSku: firstItem.stacklineSku,
      };
      if (firstItem.pixels) {
        pixelContainer = {
          brandId: firstItem.brandId,
          pixels: firstItem.pixels,
        };
      }
    }
  }

  // Validate required parameters
  if (!params?.v_id || !pixelContainer?.brandId || !pixelContainer?.pixels?.length) {
    log('track required missing params', { event: event, params: params, pixelContainer: pixelContainer });
    return;
  }

  // Group pixels by provider for efficient processing
  const pixelsByProvider = pixelContainer.pixels.reduce((acc: Record<string, TrackingPixel[]>, pixel) => {
    const provider = pixel.pixelProvider;
    acc[provider] = [...(acc[provider] || []), pixel];
    return acc;
  }, {});

  // Process each provider
  Object.entries(pixelsByProvider).forEach(([provider, providerPixels]) => {
    const trackingProvider = trackingProviders[provider];
    if (!trackingProvider) {
      log(`No provider registered for ${provider}`);
      return;
    }

    try {
      providerPixels.forEach(pixel => {
        const { eventName, eventType, eventPayload } = parseEventAndPayload(event, params, pixel.eventMapping);
        if (!eventName || !(eventPayload?.brandId || eventPayload?.stacklineSku || (eventPayload?.checkoutItems && eventPayload.checkoutItems.length > 0))) {
          log('track event not supported', event, params, pixelContainer);
          return;
        }
        trackingProvider.track(
          {
            brandId: pixelContainer.brandId,
            pixels: [pixel],
            eventName,
            eventType: eventType as BrandclubEventType,
            eventPayload
          }
        );
      });
    } catch (error) {
      log(`Error tracking with provider ${provider}:`, error);
    }
  });
}
